// Positionstabelle mit Inline-Edit (Doppelklick oder Edit-Icon -> Input, Enter=PATCH, Blur/Escape=
// verwerfen — unveraendertes Bestandsverhalten) und Abgleichs-Einfaerbung je Zelle (match/diff/
// fill/missing, Tooltip "AB x / Bestellung y"). Erweiterung "UI-Paritaet" (Bauplan Agent FE-2,
// Punkt 4): EINTEILUNG-Spalte (nach Bestellpos.), Pflichtfeld-Gelbmarkierung leerer Zellen,
// Best.-Termin-Unterzeile unter EEIND, HTN-Faerbung der MFRPN-Zelle (nach "HTN pruefen"), PO-
// Uebernahme-Chip (≠/+) fuer abweichende/fehlende Vergleichsfelder, aktives Feld-Highlight
// (Klick = nur PDF-Fundstelle hervorheben, Doppelklick = Klick-Uebernahme-Modus scharfschalten —
// s. AbDocumentDetail.js/AbPickHintBanner). Eigene Datei, damit AbDocumentDetail.js unter der
// 500-LOC-Regel bleibt.

import React, { useEffect, useState } from 'react';
import { Pencil, Trash2, AlertTriangle, Plus, Check, X } from 'lucide-react';
import { inputCls, btnGhost, iconBtnCls, compareCellCls, money, qty, fmtDate, toIsoDate } from './ui';
import { REQUIRED_FIELDS } from './detail/AbMissingFieldsPanel';

// Reihenfolge + Typ je Feld (menge/netpr = numerisch, eeind = Datum, sonst Text).
const FIELDS = ['ebeln', 'ebelp', 'labnr', 'matnr', 'mfrpn', 'menge', 'netpr', 'eeind', 'waers'];
const NUMERIC_FIELDS = new Set(['menge', 'netpr']);
const EMPTY_LINE = { ebeln: '', ebelp: '', labnr: '', matnr: '', mfrpn: '', menge: '', netpr: '', eeind: '', waers: 'EUR', einteilung: '' };
const isEmptyVal = (v) => v === null || v === undefined || v === '';

const displayValue = (field, value, currency) => {
  if (value === null || value === undefined || value === '') return '—';
  if (field === 'eeind') return fmtDate(value);
  if (field === 'netpr') return money(value, currency);
  if (field === 'menge') return qty(value);
  return String(value);
};

// EEIND-Klick-Uebernahme akzeptiert NUR ein Datum (das Feld ist ein natives type="date"-Input,
// das nur ISO YYYY-MM-DD versteht): aus dem (ggf. mehrteiligen) PDF-Klicktext wird ausschliesslich
// das Datum herausgeloest. Port von OC-Pilot DocumentDetail.tsx extractIsoDate/buildIso.
function buildIso(y, mo, d) {
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return '';
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${String(y).padStart(4, '0')}-${p(mo)}-${p(d)}`;
}
function extractIsoDateLike(v) {
  const s = String(v ?? '').trim();
  if (!s) return '';
  const iso = /(\d{4})-(\d{1,2})-(\d{1,2})/.exec(s);
  if (iso) return buildIso(+iso[1], +iso[2], +iso[3]);
  const dmy = /(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})/.exec(s);
  if (dmy) return buildIso(dmy[3].length <= 2 ? 2000 + +dmy[3] : +dmy[3], +dmy[2], +dmy[1]);
  return '';
}

/** Klickbarer "aus Bestellung uebernehmen"-Chip (amber "≠ x" bei Abweichung, blau "+ x" wenn nur die
 * Bestellung den Wert fuehrt) — Vorbild OC-Pilot poBadge(). Nur fuer Vergleichsfelder mit PO-Wert. */
function poAdoptBadge(field, cf, currency, isDarkMode, t, onAdopt) {
  if (!cf || cf.po === null || cf.po === undefined || cf.po === '') return null;
  if (cf.state !== 'diff' && cf.state !== 'fill') return null;
  const label = displayValue(field, cf.po, currency);
  const tone = cf.state === 'diff'
    ? (isDarkMode ? 'bg-amber-500/20 text-amber-300 hover:bg-amber-500/30' : 'bg-amber-100 text-amber-700 hover:bg-amber-200')
    : (isDarkMode ? 'bg-blue-500/20 text-blue-300 hover:bg-blue-500/30' : 'bg-blue-100 text-blue-700 hover:bg-blue-200');
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onAdopt(); }} title={t('abPdf.poAdoptTooltip', { value: label })}
      className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-md whitespace-nowrap ${tone}`}>
      {cf.state === 'diff' ? `≠ ${label}` : `+ ${label}`}
    </button>
  );
}

/** HTN/MFRPN-Badge (nur nach "HTN pruefen", compareLine.mfrpnState — Vorbild OC-Pilot mfrpnBadge()). */
function htnBadge(compareLine, isDarkMode, t) {
  const state = compareLine?.mfrpnState;
  if (!state || state === 'no_ampl') return null;
  const valid = Array.isArray(compareLine?.validMfrpns) && compareLine.validMfrpns.length ? compareLine.validMfrpns.join(', ') : '—';
  const cfg = {
    match: { cls: isDarkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700', label: t('abPdf.htnMatch'), title: t('abPdf.htnMatchTooltip') },
    mismatch: { cls: isDarkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700', label: t('abPdf.htnMismatch'), title: t('abPdf.htnMismatchTooltip', { valid }) },
    missing_htn: { cls: isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700', label: t('abPdf.htnMissing'), title: t('abPdf.htnMissingTooltip', { valid }) },
  }[state];
  if (!cfg) return null;
  return <span title={cfg.title} className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-md whitespace-nowrap ${cfg.cls}`}>{cfg.label}</span>;
}

/**
 * Eine editierbare Feldzelle: Anzeige, bei Doppelklick/Edit-Icon -> Input (scharft zusaetzlich das
 * Feld fuer die PDF-Klick-Uebernahme scharf); Enter = PATCH, Escape/Blur = verwerfen (Bestand).
 * Einfacher Klick (auch im Nicht-Edit-Modus) hebt nur die PDF-Fundstelle hervor (onHighlight).
 */
const EditableCell = ({
  field, value, currency, isDarkMode, t, editable, compare, onSave,
  active, fillSignal, onActivate, onDeactivate, onHighlight, requiredEmpty, extra, subline, maxLength,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(value ?? ''); }, [value]);

  // PDF-Klick-Uebernahme: nur reagieren, wenn genau diese Zelle scharfgeschaltet ist. Erster Klick
  // ersetzt, weitere haengen an (fillSignal.append) — s. AbDocumentDetail pickTarget/pickCountRef.
  useEffect(() => {
    if (!active || !fillSignal) return;
    setDraft((prev) => {
      const text = fillSignal.text ?? '';
      const merged = fillSignal.append ? `${prev} ${text}`.trim() : text;
      if (field === 'eeind') { const iso = extractIsoDateLike(merged); return iso || merged; }
      return merged;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fillSignal]);

  const cellTone = requiredEmpty ? compareCellCls('diff', isDarkMode) : (compare ? compareCellCls(compare.state, isDarkMode) : '');
  const activeRing = active ? (isDarkMode ? 'ring-1 ring-inset ring-indigo-400/70' : 'ring-1 ring-inset ring-indigo-400') : '';
  const tooltip = compare && compare.state && compare.state !== 'match'
    ? t('ab.compare.tooltip', { ab: displayValue(field, compare.ab, currency), po: displayValue(field, compare.po, currency) })
    : undefined;

  const commit = async () => {
    if (draft === (value ?? '')) { setEditing(false); onDeactivate?.(); return; }
    setSaving(true);
    try {
      await onSave(field, draft);
      setEditing(false);
      onDeactivate?.();
    } catch {
      // Fehler wird vom Aufrufer per Toast angezeigt; Eingabe bleibt offen zum Korrigieren.
    } finally {
      setSaving(false);
    }
  };
  const cancel = () => { setDraft(value ?? ''); setEditing(false); onDeactivate?.(); };
  const openEdit = () => { setEditing(true); onActivate?.(); };

  if (editing) {
    return (
      <td className={`px-2 py-1.5 ${cellTone} ${activeRing}`}>
        <input
          autoFocus
          disabled={saving}
          type={field === 'eeind' ? 'date' : 'text'}
          inputMode={NUMERIC_FIELDS.has(field) ? 'decimal' : undefined}
          maxLength={maxLength}
          value={field === 'eeind' ? toIsoDate(draft) : draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') cancel();
          }}
          onBlur={cancel}
          className={`${inputCls(isDarkMode)} w-full py-1 text-xs`}
        />
      </td>
    );
  }

  return (
    <td
      title={tooltip}
      onClick={() => onHighlight?.()}
      onDoubleClick={() => editable && openEdit()}
      className={`group px-3 py-2 text-sm ${cellTone} ${activeRing} ${editable ? 'cursor-text' : (onHighlight ? 'cursor-pointer' : '')} ${isDarkMode ? 'text-white/80' : 'text-slate-700'}`}
    >
      <span className="inline-flex items-center gap-1.5 flex-wrap">
        <span className={NUMERIC_FIELDS.has(field) ? 'tabular-nums' : ''}>{displayValue(field, value, currency)}</span>
        {extra}
        {editable && (
          <button type="button" onClick={(e) => { e.stopPropagation(); openEdit(); }}
            className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
            title={t('common.edit')}>
            <Pencil className={`w-3 h-3 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
          </button>
        )}
      </span>
      {subline}
    </td>
  );
};

/** Inline-Formular fuer eine neue, manuelle Position (inkl. Einteilung-Spalte, s. FIELDS-Fragment unten). */
const AddLineForm = ({ isDarkMode, t, onSave, onCancel }) => {
  const [draft, setDraft] = useState(EMPTY_LINE);
  const [busy, setBusy] = useState(false);
  const set = (f, v) => setDraft((d) => ({ ...d, [f]: v }));

  const save = async () => {
    setBusy(true);
    try { await onSave(draft); } finally { setBusy(false); }
  };

  const input = (f, extraProps = {}) => (
    <input
      type={f === 'eeind' ? 'date' : 'text'}
      value={draft[f]}
      onChange={(e) => set(f, e.target.value)}
      placeholder={f === 'einteilung' ? t('abPdf.einteilungLabel') : t(`ab.field.${f}`)}
      className={`${inputCls(isDarkMode)} w-full py-1 text-xs`}
      {...extraProps}
    />
  );

  return (
    <tr className={isDarkMode ? 'bg-white/[0.04]' : 'bg-slate-50'}>
      {/* Platzhalter fuer die Pos.-Spalte, damit jedes Input unter seiner Spaltenueberschrift steht */}
      <td className="px-2 py-1.5" />
      {FIELDS.map((f) => (
        <React.Fragment key={f}>
          <td className="px-2 py-1.5">{input(f)}</td>
          {f === 'ebelp' && <td className="px-2 py-1.5">{input('einteilung', { maxLength: 20 })}</td>}
        </React.Fragment>
      ))}
      <td className="px-2 py-1.5" />
      <td className="px-2 py-1.5">
        <div className="flex gap-1">
          <button type="button" disabled={busy} onClick={save} className={iconBtnCls(isDarkMode)} title={t('common.save')}>
            <Check className="w-4 h-4" />
          </button>
          <button type="button" disabled={busy} onClick={onCancel} className={iconBtnCls(isDarkMode)} title={t('common.cancel')}>
            <X className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

const AbLineTable = ({
  isDarkMode, t, lines, compareByLine, editable, onSaveField, onDeleteLine, onAddLine,
  activeField = null, onActivateField, onDeactivateField, onHighlightField, fillSignal,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [adding, setAdding] = useState(false);

  const th = `text-left text-[11px] uppercase tracking-wide font-semibold px-3 py-2 whitespace-nowrap ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`;

  const rows = Array.isArray(lines) ? lines : [];
  const adopt = (lineNo, field, v) => onSaveField(lineNo, field, v ?? '').catch(() => {});

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse min-w-[980px]">
        <thead>
          <tr className={isDarkMode ? 'border-b border-white/10' : 'border-b border-slate-200'}>
            <th className={th}>{t('ab.colPos')}</th>
            {FIELDS.map((f) => (
              <React.Fragment key={f}>
                <th className={th}>{t(`ab.field.${f}`)}</th>
                {f === 'ebelp' && <th className={th} title={t('abPdf.einteilungHint')}>{t('abPdf.einteilungLabel')}</th>}
              </React.Fragment>
            ))}
            <th className={th}>{t('ab.colWarnings')}</th>
            <th className={th}>{t('common.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((line) => {
            const compareLine = compareByLine?.get(line.lineNo);
            const warnings = Array.isArray(line.warnings) ? line.warnings : [];
            return (
              <tr key={line.id ?? line.lineNo} className={`border-b ${isDarkMode ? 'border-white/5' : 'border-slate-100'}`}>
                <td className={`px-3 py-2 text-sm tabular-nums ${isDarkMode ? 'text-white/50' : 'text-slate-400'}`}>{line.lineNo}</td>
                {FIELDS.map((f) => {
                  const cf = compareLine?.fields?.[f];
                  const isActive = activeField?.lineNo === line.lineNo && activeField?.field === f;
                  const requiredEmpty = REQUIRED_FIELDS.includes(f) && isEmptyVal(line[f]);
                  let extra = null;
                  let subline = null;
                  if (f === 'mfrpn') {
                    extra = (<>{poAdoptBadge('mfrpn', cf, line.waers, isDarkMode, t, () => adopt(line.lineNo, 'mfrpn', cf?.po))}{htnBadge(compareLine, isDarkMode, t)}</>);
                  } else if (f === 'eeind') {
                    extra = poAdoptBadge('eeind', cf, line.waers, isDarkMode, t, () => adopt(line.lineNo, 'eeind', cf?.po));
                    if (cf?.po) {
                      subline = <div className={`text-[10px] mt-0.5 ${isDarkMode ? 'text-white/35' : 'text-slate-400'}`}>{t('abPdf.eeindPoHint', { date: fmtDate(cf.po) })}</div>;
                    }
                  } else if (f === 'matnr' || f === 'menge' || f === 'netpr') {
                    extra = poAdoptBadge(f, cf, line.waers, isDarkMode, t, () => adopt(line.lineNo, f, cf?.po));
                  }
                  const cell = (
                    <EditableCell
                      key={f}
                      field={f}
                      value={line[f]}
                      currency={line.waers}
                      isDarkMode={isDarkMode}
                      t={t}
                      editable={editable}
                      compare={cf}
                      requiredEmpty={requiredEmpty}
                      extra={extra}
                      subline={subline}
                      active={isActive}
                      fillSignal={isActive ? fillSignal : null}
                      onActivate={() => onActivateField?.(line.lineNo, f)}
                      onDeactivate={() => onDeactivateField?.()}
                      onHighlight={onHighlightField ? () => onHighlightField(line.lineNo, f) : undefined}
                      onSave={(field, value) => onSaveField(line.lineNo, field, value)}
                    />
                  );
                  if (f !== 'ebelp') return cell;
                  const etActive = activeField?.lineNo === line.lineNo && activeField?.field === 'einteilung';
                  return (
                    <React.Fragment key={f}>
                      {cell}
                      <EditableCell
                        field="einteilung"
                        value={line.einteilung}
                        isDarkMode={isDarkMode}
                        t={t}
                        editable={editable}
                        maxLength={20}
                        active={etActive}
                        fillSignal={etActive ? fillSignal : null}
                        onActivate={() => onActivateField?.(line.lineNo, 'einteilung')}
                        onDeactivate={() => onDeactivateField?.()}
                        onHighlight={undefined}
                        onSave={(field, value) => onSaveField(line.lineNo, field, value)}
                      />
                    </React.Fragment>
                  );
                })}
                <td className="px-3 py-2">
                  {warnings.length > 0 && (
                    <span title={warnings.join(' · ')} className={`inline-flex items-center gap-1 text-xs ${isDarkMode ? 'text-amber-300' : 'text-amber-600'}`}>
                      <AlertTriangle className="w-3.5 h-3.5" />{warnings.length}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {editable && (
                    confirmDelete === line.lineNo ? (
                      <div className="flex items-center gap-1">
                        <span className={`text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>{t('ab.confirmDeleteLine')}</span>
                        <button type="button" onClick={() => { onDeleteLine(line.lineNo); setConfirmDelete(null); }}
                          className={iconBtnCls(isDarkMode, 'danger')} title={t('common.yes')}><Check className="w-3.5 h-3.5" /></button>
                        <button type="button" onClick={() => setConfirmDelete(null)}
                          className={iconBtnCls(isDarkMode)} title={t('common.no')}><X className="w-3.5 h-3.5" /></button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => setConfirmDelete(line.lineNo)}
                        className={iconBtnCls(isDarkMode, 'danger')} title={t('common.delete')}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )
                  )}
                </td>
              </tr>
            );
          })}
          {adding && (
            <AddLineForm isDarkMode={isDarkMode} t={t} onCancel={() => setAdding(false)}
              onSave={async (draft) => { await onAddLine(draft); setAdding(false); }} />
          )}
        </tbody>
      </table>

      {rows.length === 0 && !adding && (
        <p className={`text-sm py-6 text-center ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`}>{t('ab.noLines')}</p>
      )}

      {editable && !adding && (
        <button type="button" onClick={() => setAdding(true)} className={`${btnGhost(isDarkMode)} mt-3`}>
          <span className="flex items-center gap-2"><Plus className="w-4 h-4" />{t('ab.addLine')}</span>
        </button>
      )}
    </div>
  );
};

export default AbLineTable;
